import { Controller } from "@hotwired/stimulus"

const MAP_ID = "directories-show-map"
const DATA_SCRIPT_ID = "directory-show-map-data"

// Directory landing page map: lazy-loads Mapbox or Google Maps with office pins.
export default class extends Controller {
  static values = {
    zoom: Number,
    apiKey: String,
    accessToken: String,
    useMapbox: Boolean,
  }

  connect() {
    this.abortController = new AbortController()
    this.initialize()
  }

  disconnect() {
    this.abortController?.abort()
    this.teardown()
  }

  initialize() {
    const manager = this.mapsManager
    if (!manager) {
      console.error("Maps Manager not available")
      this.element.innerHTML =
        '<div class="flex items-center justify-center h-full text-gray-500">Maps Manager not loaded</div>'
      return
    }

    const mapOptions = this.prepareMapOptions()
    if (!mapOptions) return

    manager.registerLazyMap(this.element, mapOptions, MAP_ID, (map) => {
      this.setupMarkers(map)
    })
  }

  teardown() {
    const manager = this.mapsManager
    if (!manager) return

    if (manager.intersectionObserver) {
      manager.intersectionObserver.unobserve(this.element)
    }
    manager.lazyMaps?.delete(this.element)
    manager.destroyMap(MAP_ID)
  }

  get mapsManager() {
    return this.useMapboxValue ? window.mapboxManager : window.googleMapsManager
  }

  readOfficesData() {
    const el = document.getElementById(DATA_SCRIPT_ID)
    if (!el?.textContent?.trim()) return []
    try {
      return JSON.parse(el.textContent)
    } catch {
      return []
    }
  }

  prepareMapOptions() {
    const offices = this.readOfficesData()
    const firstOfficeWithLocation = offices.find((office) => office.latitude && office.longitude)
    if (!firstOfficeWithLocation) {
      console.warn("No offices with location data found")
      return null
    }

    if (this.useMapboxValue) {
      return {
        style: "mapbox://styles/mapbox/light-v11",
        center: [
          parseFloat(firstOfficeWithLocation.longitude),
          parseFloat(firstOfficeWithLocation.latitude),
        ],
        zoom: this.zoomValue,
      }
    }

    return {
      zoom: this.zoomValue,
      mapTypeControl: false,
      center: {
        lat: firstOfficeWithLocation.latitude,
        lng: firstOfficeWithLocation.longitude,
      },
      styles: [{ stylers: [{ saturation: -100 }] }],
    }
  }

  setupMarkers(map) {
    const offices = this.readOfficesData()
    if (!offices.length) return

    if (this.useMapboxValue) {
      this.setupMapboxMarkers(map, offices)
    } else {
      this.setupGoogleMarkers(map, offices)
    }
  }

  setupGoogleMarkers(map, offices) {
    const markers = []

    for (const office of offices) {
      if (!office.latitude || !office.longitude) continue

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(office.latitude, office.longitude),
        map,
        icon: {
          url: `/map-pin.png?v=${Date.now()}`,
          scaledSize: new google.maps.Size(32, 32),
        },
      })
      markers.push(marker)
    }

    const existingMapData = window.googleMapsManager.activeMaps.get(MAP_ID)
    if (existingMapData) {
      existingMapData.markers = markers
    }
  }

  setupMapboxMarkers(map, offices) {
    const manager = window.mapboxManager

    map.on("load", () => {
      const markers = []

      for (const office of offices) {
        if (!office.latitude || !office.longitude) continue

        const el = document.createElement("div")
        el.className = "mapbox-marker"
        el.style.width = "32px"
        el.style.height = "32px"
        el.style.cursor = "pointer"

        const img = document.createElement("img")
        img.src = "/map-pin.png"
        img.style.width = "100%"
        img.style.height = "100%"
        img.style.display = "block"
        img.alt = office.name || "Office location"
        el.appendChild(img)

        const marker = manager.createMarker({ element: el, anchor: "bottom" })
        marker.setLngLat([parseFloat(office.longitude), parseFloat(office.latitude)])
        marker.addTo(map)
        markers.push(marker)
      }

      const existingMapData = manager.activeMaps.get(MAP_ID)
      if (existingMapData) {
        existingMapData.markers = markers
      }
    })
  }
}
